import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import {
  colors,
  fonts,
  spacing,
  borderRadius,
  shadows,
  tableStyles,
} from '../constants';
import { useSnackbar } from '../components/common/Snackbar';
import { queryService } from '../services/queryService';
import { formatStatusLabel, getStatusColor, normalizeStatus } from '../utils/statusUtils';
import { formatDateTime } from '../utils/dateUtils';

const EscalationMatrix = () => {
  const { showSnackbar } = useSnackbar();
  const [allQueries, setAllQueries] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQueries = async () => {
    setLoading(true);
    try {
      // Fetch all queries, we'll filter for escalated/delegated client-side
      const params = { 
        page: 1,
        limit: 1000,
      };
      const response = await queryService.getQueries(params);
      const queries = response.queries || [];
      // Filter for escalated and delegated queries
      const escalatedQueries = queries.filter(query => {
        const status = normalizeStatus(query.status || query.statusLabel);
        return status === 'escalated' || status === 'delegated';
      });
      setAllQueries(escalatedQueries);
    } catch (error) {
      // console.error('Failed to fetch escalation data:', error);
      const message =
        error.response?.data?.message || 'Failed to load escalation data';
      showSnackbar(message, 'error');
      setAllQueries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setPriorityFilter('');
    setRegionFilter('');
    setStatusFilter('active');
    setPage(0);
  };

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, priorityFilter, regionFilter, statusFilter]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
      case 'urgent':
        return colors.error;
      case 'medium':
        return colors.warning;
      case 'low':
        return colors.info;
      default:
        return colors.textSecondary;
    }
  };

  const baseFilteredData = useMemo(() => {
    const trimmedSearch = searchQuery.trim().toLowerCase();

    return allQueries.filter((query) => {
      const priority = query.priority?.toLowerCase() || '';
      const region = query.region || '';

      const matchesSearch =
        trimmedSearch.length === 0 ||
        query.queryId?.toLowerCase().includes(trimmedSearch) ||
        query.queryHeader?.toLowerCase().includes(trimmedSearch) ||
        query.queryDescription?.toLowerCase().includes(trimmedSearch) ||
        query.projectName?.toLowerCase().includes(trimmedSearch) ||
        query.assignee?.full_name?.toLowerCase().includes(trimmedSearch) ||
        query.assignee?.name?.toLowerCase().includes(trimmedSearch) ||
        query.escalatedTo?.full_name?.toLowerCase().includes(trimmedSearch) ||
        query.escalatedTo?.name?.toLowerCase().includes(trimmedSearch) ||
        query.delegatedTo?.full_name?.toLowerCase().includes(trimmedSearch) ||
        query.delegatedTo?.name?.toLowerCase().includes(trimmedSearch);

      const matchesPriority =
        !priorityFilter || priority === priorityFilter.toLowerCase();

      const matchesRegion =
        !regionFilter || region.toLowerCase() === regionFilter.toLowerCase();

      return matchesSearch && matchesPriority && matchesRegion;
    });
  }, [allQueries, searchQuery, priorityFilter, regionFilter]);

  const regionOptions = useMemo(() => {
    const regions = new Set();
    allQueries.forEach((query) => {
      if (query.region) {
        regions.add(query.region);
      }
    });
    return Array.from(regions).sort();
  }, [allQueries]);

  const statusCounts = useMemo(() => {
    return baseFilteredData.reduce(
      (acc, query) => {
        const status = normalizeStatus(query.status);
        if (query.isClosed === true || status === 'closed') {
          acc.closed += 1;
        } else {
          acc.active += 1;
        }
        if (status === 'escalated') acc.escalated += 1;
        if (status === 'delegated') acc.delegated += 1;
        if (status === 'responded') acc.responded += 1;
        return acc;
      },
      { active: 0, closed: 0, escalated: 0, delegated: 0, responded: 0 }
    );
  }, [baseFilteredData]);

  const filteredData = useMemo(() => {
    return baseFilteredData.filter((query) => {
        const status = normalizeStatus(query.status);
      switch (statusFilter) {
        case 'all':
          return true;
        case 'closed':
          return query.isClosed === true || status === 'closed';
        case 'escalated':
          return status === 'escalated';
        case 'delegated':
          return status === 'delegated';
        case 'responded':
          return status === 'responded';
        case 'active':
        default:
          return !(query.isClosed === true || status === 'closed');
      }
    });
  }, [baseFilteredData, statusFilter]);

  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  const statusOptions = [
    { value: 'active', label: `Active (${statusCounts.active})` },
    { value: 'escalated', label: `Escalated (${statusCounts.escalated})` },
    { value: 'delegated', label: `Delegated (${statusCounts.delegated})` },
    { value: 'responded', label: `Responded (${statusCounts.responded})` },
    { value: 'closed', label: `Closed (${statusCounts.closed})` },
    { value: 'all', label: 'All' },
  ];

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: spacing.md }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>


        {/* Header with toggle button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: spacing.xs,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
            <FilterListIcon sx={{ color: colors.primary, fontSize: '20px' }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: fonts.weightSemiBold,
                fontSize: fonts.fontSizeMd,
                color: colors.textPrimary,
              }}
            >
              Search & Filters
            </Typography>
            {(searchQuery || statusFilter !== 'active' || priorityFilter || regionFilter) && (
              <Chip
                label={
                  [
                    searchQuery && 'Search',
                    statusFilter !== 'active' && 'Status',
                    priorityFilter && 'Priority',
                    regionFilter && 'Region',
                  ]
                    .filter(Boolean)
                    .length
                }
                size="small"
                sx={{
                  height: '20px',
                  fontSize: fonts.fontSizeXs,
                  backgroundColor: colors.primary + '20',
                  color: colors.primary,
                }}
              />
            )}
          </Box>
          <Tooltip title={isFilterExpanded ? 'Collapse filters' : 'Expand filters'} arrow>
            <IconButton
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              size="small"
              sx={{
                color: colors.primary,
                '&:hover': {
                  backgroundColor: colors.primaryTint,
                },
              }}
            >
              {isFilterExpanded ? (
                <ExpandLessIcon sx={{ fontSize: '24px' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: '24px' }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Collapsible filter content */}
        <Collapse in={isFilterExpanded}>
          <Box sx={{ mb: spacing.md, display: 'flex', gap: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search by name, email, or project..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.textSecondary, fontSize: '18px' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <ClearIcon
                      sx={{ cursor: 'pointer', color: colors.textSecondary, fontSize: '16px' }}
                      onClick={() => setSearchQuery('')}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: '200px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent',
                  height: '40px',
                },
              }}
            />
            <TextField
              select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              SelectProps={{
                native: true,
              }}
              sx={{
                minWidth: '120px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent',
                  height: '40px',
                },
              }}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <TextField
              select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              size="small"
              SelectProps={{
                native: true,
              }}
              sx={{
                minWidth: '140px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent',
                  height: '40px',
                },
              }}
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <TextField
              select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              size="small"
              InputProps={{
                endAdornment:
                  regionFilter && (
                    <InputAdornment position="end">
                      <ClearIcon
                        sx={{ cursor: 'pointer', color: colors.textSecondary, fontSize: '16px' }}
                        onClick={() => setRegionFilter('')}
                      />
                    </InputAdornment>
                  ),
              }}
              SelectProps={{
                native: true,
              }}
              sx={{
                minWidth: '150px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent',
                  height: '40px',
                },
              }}
            >
              <option value="">All Regions</option>
              {regionOptions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </TextField>
            {(searchQuery || statusFilter !== 'active' || priorityFilter || regionFilter) && (
              <Chip
                label="Clear All"
                onClick={handleClearFilters}
                onDelete={handleClearFilters}
                size="small"
                sx={{
                  height: '40px',
                  fontSize: fonts.fontSizeSm,
                }}
              />
            )}
          </Box>
        </Collapse>

        <Paper sx={{ borderRadius: borderRadius.md, boxShadow: shadows.md }}>
          <Box sx={{ p: spacing.sm, borderBottom: `1px solid ${colors.borderDefault}` }}>
            <Typography variant="h6" sx={{ fontWeight: fonts.weightBold, fontSize: fonts.fontSizeMd }}>
              Escalation Matrix
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.backgroundSoft }}>
                  <TableCell sx={tableStyles.header}>Query ID</TableCell>
                  {/* <TableCell sx={tableStyles.header}>Title</TableCell> */}
                  <TableCell sx={tableStyles.header}>Status</TableCell>
                  <TableCell sx={tableStyles.header}>Priority</TableCell>
                  <TableCell sx={tableStyles.header}>Region</TableCell>
                  <TableCell sx={tableStyles.header}>Current Assignee</TableCell>
                  <TableCell sx={tableStyles.header}>Escalated To</TableCell>
                  <TableCell sx={tableStyles.header}>Delegated To</TableCell>
                  <TableCell sx={tableStyles.header}>Raised By</TableCell>
                  <TableCell sx={tableStyles.header}>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        No queries match the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => (
                    <TableRow key={row._id} hover sx={{ '&:hover': { backgroundColor: colors.backgroundLight } }}>
                      <TableCell sx={{ ...tableStyles.cell, fontWeight: fonts.weightMedium }}>
                        {row.queryId || row._id}
                      </TableCell>
                      {/* <TableCell sx={{ ...tableStyles.cell }}>
                        {row.queryHeader || row.queryDescription?.substring(0, 50) || 'N/A'}
                      </TableCell> */}
                      <TableCell sx={{ ...tableStyles.cell }}>
                        <Chip
                          label={formatStatusLabel(row.status, { uppercase: true })}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(row.status) + '20',
                            color: getStatusColor(row.status),
                            fontWeight: fonts.weightSemiBold,
                            fontSize: fonts.fontSizeXs,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        <Chip
                          label={(row.priority || 'N/A').toUpperCase()}
                          size="small"
                          sx={{
                            backgroundColor: getPriorityColor(row.priority) + '20',
                            color: getPriorityColor(row.priority),
                            fontWeight: fonts.weightSemiBold,
                            fontSize: fonts.fontSizeXs,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        {row.region || '—'}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        {row.assignee?.full_name || row.assignee?.name || 'Unassigned'}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        {row.escalatedTo?.full_name || row.escalatedTo?.name || '—'}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        {row.delegatedTo?.full_name || row.delegatedTo?.name || '—'}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        {row.raisedBy?.full_name || row.raisedBy?.name || '—'}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        <Chip
                          label={formatDateTime(row.updatedAt || row.createdAt || row.raisedAt)}
                          size="small"
                          sx={{
                            backgroundColor: colors.backgroundSoft,
                            color: colors.textSecondary,
                            fontWeight: fonts.weightMedium,
                            fontSize: fonts.fontSizeXs,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: `1px solid ${colors.borderDefault}`,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: fonts.fontSizeXs,
                color: colors.textSecondary,
              },
              '& .MuiIconButton-root': {
                color: colors.textSecondary,
                '&:hover': {
                  backgroundColor: colors.backgroundSoft,
                },
              },
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default EscalationMatrix;

